import React, { CSSProperties, useEffect, useRef } from 'react';

type ParticleConfig = {
  count: number;
  colors: string[];
  speed: number;
  size: number;
  lifetime: number;
  spread: number;
  gravity: number;
};

const effectConfigs: Record<string, ParticleConfig> = {
  fold: {
    count: 15,
    colors: ['#888', '#666', '#aaa', '#555'],
    speed: 3,
    size: 4,
    lifetime: 600,
    spread: Math.PI * 2,
    gravity: 0.08,
  },
  check: {
    count: 12,
    colors: ['#fff', '#ddd', '#E98C26', '#d4a853'],
    speed: 4,
    size: 3,
    lifetime: 500,
    spread: Math.PI * 2,
    gravity: 0.02,
  },
  call: {
    count: 20,
    colors: ['#4CAF50', '#81C784', '#A5D6A7', '#66BB6A'],
    speed: 5,
    size: 3,
    lifetime: 600,
    spread: Math.PI * 2,
    gravity: 0.03,
  },
  raise: {
    count: 30,
    colors: ['#E98C26', '#FFD700', '#FFA500', '#d4a853', '#fff'],
    speed: 6,
    size: 4,
    lifetime: 800,
    spread: Math.PI * 2,
    gravity: -0.02,
  },
  allin: {
    count: 40,
    colors: ['#E98C26', '#FFD700', '#FF4500', '#fff', '#FFA500'],
    speed: 8,
    size: 5,
    lifetime: 1000,
    spread: Math.PI * 2,
    gravity: -0.03,
  },
};

type ActionEffectProps = {
  variant: string;
  trigger: number; // increment to trigger
};

const ActionEffect = ({ variant, trigger }: ActionEffectProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (trigger === 0 || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const config = effectConfigs[variant] || effectConfigs.check;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    type Particle = {
      x: number; y: number; vx: number; vy: number;
      size: number; color: string; life: number; maxLife: number;
    };

    const particles: Particle[] = [];
    for (let i = 0; i < config.count; i++) {
      const angle = Math.random() * config.spread - config.spread / 2 - Math.PI / 2;
      const speed = config.speed * (0.5 + Math.random() * 0.5);
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: config.size * (0.5 + Math.random()),
        color: config.colors[Math.floor(Math.random() * config.colors.length)],
        life: config.lifetime * (0.7 + Math.random() * 0.3),
        maxLife: config.lifetime,
      });
    }

    const start = performance.now();
    let frame: number;

    const animate = (now: number) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let alive = false;
      for (const p of particles) {
        const t = elapsed / p.life;
        if (t >= 1) continue;
        alive = true;

        p.x += p.vx;
        p.y += p.vy;
        p.vy += config.gravity;

        const alpha = 1 - t;
        const size = p.size * (1 - t * 0.5);

        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      if (alive) {
        frame = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [trigger, variant]);

  return <canvas ref={canvasRef} style={canvasStyle} />;
};

export default ActionEffect;

const canvasStyle: CSSProperties = {
  position: 'absolute',
  top: '-50%',
  left: '-25%',
  width: '150%',
  height: '200%',
  pointerEvents: 'none',
  zIndex: 10,
};
